/**
 * TrainingAudioExportQueue
 *
 * Shows training session audio export jobs for lecturers.
 * Uses the lecturer audio exports API endpoints.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Button,
  CircularProgress, LinearProgress, Tooltip, Alert,
  Card, CardContent, Grid
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { formatDistanceToNow } from 'date-fns';
import trainingApi from '../../services/trainingApi';

const TrainingAudioExportQueue = () => {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchExports = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const response = await trainingApi.listAudioExports();
      const body = response?.data || response;
      const data = body?.data || body;
      setExports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching training audio exports:', err);
      setError('Failed to load audio exports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchExports(); }, []);

  // Auto-refresh for active jobs
  useEffect(() => {
    const hasActive = exports.some(e => e.status === 'pending' || e.status === 'processing');
    if (hasActive) {
      const interval = setInterval(() => fetchExports(false), 10000);
      return () => clearInterval(interval);
    }
  }, [exports]);

  const handleRefresh = () => { setRefreshing(true); fetchExports(); };

  const handleDownload = async (job) => {
    try {
      const response = await trainingApi.downloadAudioExport(job._id);
      const blob = response.data;
      // If response is JSON (e.g. status update), not a file
      if (blob.type === 'application/json') return;
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.download = filenameMatch?.[1] || `training-session-audio-${job._id}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading audio export:', err);
      setError('Failed to download audio file');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'processing': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <HourglassEmptyIcon fontSize="small" />;
      case 'processing': return <CircularProgress size={16} />;
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      default: return null;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const stats = exports.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, { pending: 0, processing: 0, completed: 0, failed: 0 });

  if (loading && exports.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Training Audio Exports</Typography>
        <Button
          size="small"
          startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {['pending', 'processing', 'completed', 'failed'].map(status => (
          <Grid item xs={6} sm={3} key={status}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                  {status === 'completed' ? 'Ready' : status}
                </Typography>
                <Typography
                  variant="h5"
                  color={
                    status === 'completed' ? 'success.main' :
                    status === 'failed' ? 'error.main' :
                    status === 'processing' ? 'primary.main' : 'text.primary'
                  }
                >
                  {stats[status] || 0}
                </Typography>
                {status === 'processing' && (stats.processing || 0) > 0 && <LinearProgress sx={{ mt: 0.5 }} />}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Export Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Session</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Messages</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Size</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No training audio exports yet. Use the "Export Audio" button in a session review to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              exports.map(job => (
                <TableRow key={job._id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {job.clientCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {job.requestedAt ? formatDistanceToNow(new Date(job.requestedAt), { addSuffix: true }) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={job.status} color={getStatusColor(job.status)} size="small" icon={getStatusIcon(job.status)} />
                  </TableCell>
                  <TableCell>{job.messageCount || '-'}</TableCell>
                  <TableCell>{job.duration || '-'}</TableCell>
                  <TableCell>{formatFileSize(job.fileSize)}</TableCell>
                  <TableCell align="center">
                    {job.status === 'completed' && (
                      <Tooltip title="Download MP3">
                        <IconButton size="small" onClick={() => handleDownload(job)} color="primary">
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TrainingAudioExportQueue;
